import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Search, Plus } from 'lucide-react';
import { PopoverDialog, PopoverDialogContent, PopoverDialogTrigger } from '@/components/ui/popover-dialog';
import {
  CommandDialog,
  CommandDialogEmpty,
  CommandDialogGroup,
  CommandDialogInput,
  CommandDialogItem,
} from "@/components/ui/command-dialog";

interface Item {
  id: string;
  name: string;
  [key: string]: any;
}

interface SingleSelectProps {
  items: Item[];
  selectedId: string | undefined;
  onSelectItem: (id: string) => void;
  placeholder: string;
  emptyMessage: string;
  searchPlaceholder: string;
  disabled?: boolean;
  showAddButton?: boolean;
  onAddClick?: () => void;
  width?: string;
  addButtonLabel?: string;
  className?: string;
}

export function SingleSelect({
  items,
  selectedId,
  onSelectItem,
  placeholder,
  emptyMessage,
  searchPlaceholder,
  disabled = false,
  showAddButton = false,
  onAddClick,
  width = "300px xs:w-[350px]",
  addButtonLabel = "Add new",
  className = "",
}: SingleSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Find the selected item to display its name
  const selectedItem = items.find(item => item.id === selectedId);

  // Filter items based on search query with improved filtering
  const filteredItems = React.useMemo(() => {
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase().trim();
    
    return items.filter(item => {
      // Handle potentially malformed or null item names
      if (!item.name) return false;
      
      // Normalize the item name: trim whitespace and lowercase
      const normalizedName = item.name.toLowerCase().trim();
      
      // Check if the normalized name includes the query
      return normalizedName.includes(query);
    });
  }, [items, searchQuery]);

  return (
    <div className={`flex flex-nowrap items-center gap-2 ${className}`}>
      <PopoverDialog open={isOpen} onOpenChange={setIsOpen}>
        <PopoverDialogTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={isOpen}
            className="flex-grow justify-between min-w-0"
            disabled={disabled}
          >
            {selectedItem?.name || placeholder}
            <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverDialogTrigger>
        <PopoverDialogContent className={`w-[${width}] p-0`}>
          <CommandDialog>
            <CommandDialogInput
              placeholder={searchPlaceholder}
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandDialogEmpty>{emptyMessage}</CommandDialogEmpty>
            <CommandDialogGroup className="max-h-60 overflow-y-auto">
              {filteredItems.map((item) => (
                <CommandDialogItem
                  key={item.id}
                  value={item.id}
                  onSelect={() => {
                    onSelectItem(item.id);
                    setIsOpen(false);
                  }}
                >
                  {item.name}
                </CommandDialogItem>
              ))}
            </CommandDialogGroup>
          </CommandDialog>
        </PopoverDialogContent>
      </PopoverDialog>
      
      {showAddButton && (
        <Button 
          type="button" 
          variant="outline" 
          size="icon"
          onClick={onAddClick}
          disabled={disabled}
          aria-label={addButtonLabel}
        >
          <Plus className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
} 